import { GroupResponse, GroupResponseV2, GroupType } from 'api/httpEntities';
import { Box, Card, CardContent, Link, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import GroupSvg from 'components/Svgs/icons/Group';
import { TextV2 } from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

type GroupCardProps = { group: GroupResponse | GroupResponseV2 };

const StyledGroupSvg = styled(GroupSvg)(({ theme }) => ({
  fill: theme.palette.grey[500],
}));

const StyledP = styled(TextV2)({});
const StyledDiv = styled('div')(({ theme }) => ({
  position: 'relative',
  top: '24px',
  minWidth: '48px',
  minHeight: '48px',
  backgroundColor: theme.palette.common.white,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  display: 'flex',
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '5px',
}));

const GroupCard = ({ group }: GroupCardProps) => {
  const { id, name, type } = group;
  const organisations =
    'organisations' in group ? group.organisations : undefined;
  const organisationsCount =
    'organisationsCount' in group ? group.organisationsCount : undefined;
  const { t } = useTranslation();

  const count = organisations?.length ?? organisationsCount ?? 0;

  const countLabel =
    count === 1
      ? 'ui.event-management.organisations.group.organisation-count.singular'
      : 'ui.event-management.organisations.group.organisation-count.plural';

  const groupTypeLabel: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.organisations.group.label.customer'),
    PARTNER: t('ui.event-management.organisations.group.label.partner'),
  };

  return (
    <Grid
      data-testid={`group-card-${id}`}
      key={id}
      sx={{
        overflow: 'hidden',
        minHeight: '300px',
      }}
      size={{ xs: 12, md: 4 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          marginTop: 0,
        }}
      >
        <StyledDiv>
          <StyledGroupSvg
            title={t('ui.event-management.organisations.group.alt.icon')}
          />
        </StyledDiv>
      </Box>
      <Card
        variant="outlined"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          minHeight: 'calc(100% - 48px)',
          padding: '0',
          borderColor: theme.palette.grey[500],
        })}
      >
        <CardContent
          sx={{
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'center',
          }}
        >
          <Box
            sx={(theme) => ({
              backgroundColor:
                type === 'PARTNER'
                  ? theme.palette.green.light
                  : theme.palette.orange.light,
              width: '100%',
              minHeight: '107px',
              margin: '0',
              padding: '2rem 1rem 1rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            })}
          >
            <Link
              component={RouterLink}
              to={`/groups/${id}`}
              sx={(theme) => ({
                width: '100%',
                fontSize: '1.5rem',
                lineHeight: '30px !important',
                fontWeight: theme.typography.fontWeightBold,
                color: `${theme.palette.blue.main} !important`,
                '&:hover': {
                  color: `${theme.palette.blue.dark} !important`,
                },
                '&:active': {
                  color: `${theme.palette.text.primary} !important`,
                },
                wordBreak: 'break-word',
              })}
            >
              {name}
            </Link>
            <StyledP
              component="p"
              sx={{ lineHeight: '28px !important' }}
              color="textSecondary"
              variant="bodyBold"
            >
              {groupTypeLabel[type]}
            </StyledP>
          </Box>
          <Grid
            container
            sx={{ justifyContent: 'center', flexDirection: 'column' }}
          >
            <StyledP
              component="p"
              variant="bodyBold"
              color="primary"
              sx={{
                marginTop: '12px !important',
                lineHeight: '28px !important',
              }}
            >
              {t(countLabel, { count })}
            </StyledP>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default GroupCard;
